import { gstinProblem } from './gst';
import { i18n } from './i18n';
import { ltrIn } from './sales';

/** What the customer/vendor drawer checks before saving (document 06 S11, E9). */
export interface ContactCheck {
  kind: 'customer' | 'vendor';
  displayName: string;
  email: string;
  /** Billing address country (ISO 3166 alpha-2). */
  country: string;
  gstin: string;
  trn: string;
  vatRegistered: boolean;
  iban: string;
}

const IBAN = /^[A-Z]{2}[0-9]{2}[A-Z0-9]{11,30}$/;
const INDIAN_ACCOUNT = /^[0-9]{9,18}[A-Z]{4}0[A-Z0-9]{6}$/;

/** IBAN or account number + IFSC as the API stores it: no spaces, slashes, colons or dashes. */
export const normaliseIban = (s: string) => s.replace(/[\s/:-]+/g, '').toUpperCase();

/**
 * Field errors keyed like the API's validation details (`displayName`, `email`, `trn`, `gstin`,
 * `iban`); empty when the contact can be saved. Mirrors apps/api/src/contacts/dto.ts.
 */
export function contactProblems(c: ContactCheck, india: boolean): Record<string, string> {
  const next: Record<string, string> = {};
  const trn = c.trn.replace(/[\s-]+/g, '');
  if (!c.displayName.trim())
    next['displayName'] = i18n.t('salesPages.contactChecks.enterDisplayName');
  if (c.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(c.email.trim()))
    next['email'] = i18n.t('settingsPages.company.companyTab.invalidEmail');
  if (c.country === 'IN' && c.gstin.trim()) {
    const problem = gstinProblem(c.gstin);
    if (problem) next['gstin'] = problem;
  }
  if (c.country === 'AE') {
    if (trn && !/^[0-9]{15}$/.test(trn))
      next['trn'] = i18n.t('settingsPages.company.companyTab.trnDigits');
    if (c.vatRegistered && !trn) next['trn'] = i18n.t('salesPages.contactChecks.vatNeedsTrn');
  }
  const iban = normaliseIban(c.iban);
  if (c.kind === 'vendor' && iban) {
    // the API takes either form; an India client mostly pays Indian accounts, a foreign vendor
    // still has an IBAN
    if (!IBAN.test(iban) && !INDIAN_ACCOUNT.test(iban))
      next['iban'] = india
        ? i18n.t('salesPages.contactChecks.accountOrIban', {
            example: ltrIn('50200012345678 HDFC0000123'),
          })
        : i18n.t('salesPages.contactChecks.invalidIban', {
            example: ltrIn('AE07 0331 2345 6789 0123 456'),
          });
    else if (/^AE[0-9]/.test(iban) && iban.length !== 23)
      next['iban'] = i18n.t('salesPages.contactChecks.uaeIbanLength');
  }
  return next;
}
